import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { eventLabel, fieldLabel } from '../../utils/labels.js';
import { automationApi } from '../../services/automationApi.js';

const OPS = ['lt', 'lte', 'gt', 'gte', 'eq', 'neq', 'contains', 'exists'];
const SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'];
const empty = () => ({ name: '', triggerEvent: '', conditionField: '', conditionOp: 'gte', conditionValue: '', severity: 'WARNING', message: '' });

export function RuleCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState(null);

  const triggers = useQuery({ queryKey: ['automation', 'triggers'], queryFn: () => automationApi.listTriggers().then((r) => r.items), enabled: open });

  useEffect(() => { if (open) { setForm(empty()); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const fields = useMemo(() => (triggers.data || []).find((tr) => tr.event === form.triggerEvent)?.fields || [], [triggers.data, form.triggerEvent]);

  const save = useMutation({
    mutationFn: (body) => automationApi.createRule(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['automation', 'rules'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim() || !form.triggerEvent || !form.message.trim()) { setError(t('auto.error.required')); return; }
    save.mutate({
      name: form.name.trim(),
      triggerEvent: form.triggerEvent,
      conditionField: form.conditionField || null,
      conditionOp: form.conditionField ? form.conditionOp : null,
      conditionValue: form.conditionField && form.conditionOp !== 'exists' ? form.conditionValue : null,
      severity: form.severity,
      message: form.message.trim(),
    });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('auto.modal.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <label className="field">
          <span className="field-label">{t('auto.field.name')}</span>
          <input className="input" value={form.name} onChange={set('name')} required />
        </label>
        <label className="field">
          <span className="field-label">{t('auto.field.trigger')}</span>
          <select className="input" value={form.triggerEvent} onChange={(e) => setForm((f) => ({ ...f, triggerEvent: e.target.value, conditionField: '' }))} required>
            <option value="">-</option>
            {(triggers.data || []).map((tr) => <option key={tr.event} value={tr.event}>{eventLabel(t, tr.event)}</option>)}
          </select>
        </label>

        <fieldset className="field" style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-2) var(--space-3)' }}>
          <span className="field-label">{t('auto.field.condition')}</span>
          <div className="field-row">
            <select className="input" value={form.conditionField} onChange={set('conditionField')} disabled={!form.triggerEvent}>
              <option value="">{t('auto.field.noCondition')}</option>
              {fields.map((fld) => <option key={fld} value={fld}>{fieldLabel(t, fld)}</option>)}
            </select>
            <select className="input" style={{ maxWidth: 110 }} value={form.conditionOp} onChange={set('conditionOp')} disabled={!form.conditionField}>
              {OPS.map((op) => <option key={op} value={op}>{t(`auto.op.${op}`)}</option>)}
            </select>
            <input className="input" placeholder={t('auto.field.value')} value={form.conditionValue} onChange={set('conditionValue')}
              disabled={!form.conditionField || form.conditionOp === 'exists'} />
          </div>
        </fieldset>

        <div className="field-row">
          <label className="field" style={{ maxWidth: 180 }}>
            <span className="field-label">{t('auto.field.severity')}</span>
            <select className="input" value={form.severity} onChange={set('severity')}>
              {SEVERITIES.map((s) => <option key={s} value={s}>{t(`auto.severity.${s}`)}</option>)}
            </select>
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="field-label">{t('auto.field.message')}</span>
            <input className="input" value={form.message} onChange={set('message')} placeholder="{sku} …" required />
          </label>
        </div>
        <p className="muted text-sm">{t('auto.field.messageHint')}{fields.length > 0 ? ` ${fields.map((f) => `{${f}}`).join(', ')}` : ''}</p>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
